import React from 'react';
import { Shield, Leaf, Trash2, Building2, Clock } from 'lucide-react';

const GuidelinesPage = () => {
  const guidelines = [
    {
      icon: Shield,
      title: "Regulatory Compliance and Coordination Framework",
      content: "All waterbody strengthening work must be conducted under proper departmental guidance with mandatory pre-work notifications to relevant engineering divisions, ensuring compliance with hydrological guidelines and established policies for waterbody management."
    },
    {
      icon: Leaf,
      title: "Buffer Zone Protection and Bio Fencing Implementation",
      content: "Projects must maintain natural water flow without embankment construction, while establishing protective buffer zones through Bio Fencing with trees and plants around waterbody perimeters to comply with waterbody protection norms and ecological conservation requirements."
    },
    {
      icon: Trash2,
      title: "Waste Management and Environmental Compliance",
      content: "Dredging operations must follow strict waste disposal regulations by removing all garbage and debris to authorized solid waste management centers, preventing environmental contamination and ensuring compliance with local waste management policies."
    },
    {
      icon: Building2,
      title: "Construction Standards and Zoning Guidelines",
      content: "Excavated soil utilization must follow engineering specifications for constructing habitat embankments while maintaining proper water surface levels, adhering to zoning regulations that balance ecological needs with slope stabilization through deep-rooted vegetation."
    },
    {
      icon: Clock,
      title: "Timeline Enforcement and Public Transparency Framework",
      content: "Projects must be completed within the legally mandated 3-month timeframe with mandatory public notice boards displaying contractor details, costs, and project specifications, ensuring regulatory timeline compliance while maintaining transparency standards and photo documentation requirements."
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');
        
        .guidelines-font {
          font-family: 'Inter', system-ui, sans-serif;
        }
        
        body, html {
          margin: 0;
          padding: 0;
          min-height: 100vh;
        }
      `}</style>
      
      <section className="py-36 guidelines-font">
        <div className="container mx-auto px-6 lg:px-8 max-w-5xl">
          
          {/* Header Section */}
          <div className="text-center mb-12">
            <h1 className="text-3xl lg:text-4xl font-semibold text-slate-800 mb-4">
              Waterbodies Strengthening Guidelines
            </h1>
            <p className="text-slate-600 text-lg max-w-2xl mx-auto">
              Comprehensive regulatory framework and operational standards for project implementation
            </p>
          </div>

          {/* Guidelines Section */}
          <div className="bg-slate-100 rounded-2xl border-2 border-slate-400 p-8 shadow-xl mb-10">
            <h2 className="text-xl font-semibold text-slate-800 mb-6">
              Terms and Conditions
            </h2>
            
            <div className="space-y-6">
              {guidelines.map((guideline, index) => {
                const IconComponent = guideline.icon;
                return (
                  <div 
                    key={index}
                    className="flex items-start space-x-4 p-4 rounded-xl border-2 border-slate-400 bg-slate-200"
                  >
                    <div className="w-8 h-8 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-full flex items-center justify-center text-white flex-shrink-0 shadow-lg">
                      <IconComponent size={14} />
                    </div>
                    <div>
                      <h3 className="font-medium text-slate-800 mb-2">
                        {guideline.title}
                      </h3>
                      <p className="text-slate-600 text-sm leading-relaxed">
                        {guideline.content}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Compliance Framework */}
          <div className="bg-amber-50 rounded-2xl border-2 border-orange-200 p-8 shadow-xl">
            <h2 className="text-xl font-semibold text-slate-800 mb-4">
              Compliance Framework
            </h2>
            <p className="text-slate-600 leading-relaxed">
              All strengthening projects must adhere to these guidelines to ensure environmental sustainability, 
              regulatory compliance, and successful project delivery within established timelines and quality standards. 
              Non-compliance with any of these requirements may result in project suspension or termination.
            </p>
          </div>

        </div>
      </section>
    </div>
  );
};

export default GuidelinesPage;